import { Injectable } from '@angular/core';
import { isEmpty, chain, some } from 'lodash';
import { MultilineFormatter } from '../../shared';
import { FilterGridColDef, FilterFieldType } from '../../covance-filter';
import { dateTimeRenderer, MultilineAgRendererComponent, dateTimeComparator } from '../../covance-ag-grid';
import { MicrobiologyResultAgRendererComponent } from './microbiology-result-ag-renderer.component';
import { MicrobiologyData } from './microbiology-data';
import { MicrobiologyGridRowItem } from './microbiology-grid-row-item';
import { SubjectTitleRenderer } from '../../renderer/subject-title-renderer';
import { OnScreenResultsRenderer } from '../on-screen-results-renderer';

@Injectable()
export class MicrobiologyGridColumns {

    public get columnsDict(): { [microBiologyCategory: string]: FilterGridColDef[] } {
        return {
            'isolate-culture-data': [...this.commonColumns, ...this.isolateCultureColumns],
            'gram-stain-data': [...this.commonColumns, ...this.gramStainColumns]
        };
    }
    
    public get pivotColumns(): FilterGridColDef[] {
        return [...this.microbiologyPivotColumns];
    }


    private microbiologyPivotColumns: FilterGridColDef[] = [
        {
            headerName: 'Drug/Agent',
            field: 'agent'
        },
        {
            hide: true,
            headerName: 'Specimen',
            field: 'specimen'
        },
        {
            hide: true,
            headerName: 'Organism',
            field: 'organism',

            getLineCount: (entity: MicrobiologyData) => {
                return this.multilineFormatter.format(entity.organism).length;
            },

            cellRendererFramework: MultilineAgRendererComponent
        },
        {
            hide: true,
            headerName: 'Panel',
            field: 'panel'
        }
    ];

    private commonColumns: FilterGridColDef[] = [
        {
            headerName: 'Subject',
            field: 'initials',
            valueGetter: SubjectTitleRenderer.renderTitle,
            filterFieldDef: {
                type: FilterFieldType.MultiSelect,
                order: 200,
                isMatch: (selectedValues: string[], entity: MicrobiologyData) => {
                    const subjectTitle = SubjectTitleRenderer.renderTitle({ data: entity });
                    return some(selectedValues, val => val === subjectTitle);
                },
                getItems: (entities: MicrobiologyData[]) => {
                    const items = chain(entities)
                        .map((entity: MicrobiologyData) => SubjectTitleRenderer.renderTitle({ data: entity }))
                        .uniq()
                        .sortBy()
                        .value();

                    return items;
                }
            }
        },
        {
            headerName: 'Screen No.',
            field: 'screeningNumber',
            filterFieldDef: {
                type: FilterFieldType.MultiSelect,
                order: 250
            }
        },
        {
            headerName: 'Age',
            field: 'daysLived',
            filterFieldDef: {
                order: 270,
                type: FilterFieldType.AgeRange
            }
        },
        {
            headerName: 'Gender',
            field: 'sex',
            filterFieldDef: {
                type: FilterFieldType.MultiSelect,
                order: 300
            }
        },
        {
            headerName: 'Visit',
            field: 'visitDescription',
            valueGetter: (params: MicrobiologyGridRowItem) => this.getVisitDescriptionRenderer(params.data),
            filterFieldDef: {
                type: FilterFieldType.MultiSelect,
                order: 400,
                isMatch: (selectedValues: string[], entity: MicrobiologyData) => {
                    const result = this.getVisitDescriptionRenderer(entity);
                    return some(selectedValues, val => val === result);
                },
                getItems: (entities: MicrobiologyData[]) => {
                    const items = chain(entities)
                        .map((entity: MicrobiologyData) => this.getVisitDescriptionRenderer(entity))
                        .uniq()
                        .sortBy()
                        .value();

                    return items;
                }
            }
        },
        {
            headerName: 'Collected Date',
            field: 'collectionDateTime',
            comparator: dateTimeComparator,
            valueGetter: (params: MicrobiologyGridRowItem) => {
                if (!params.data) {
                    return null;
                }
                return dateTimeRenderer({ value: params.data.collectionDateTime });
            },
            filterFieldDef: {
                type: FilterFieldType.DateRange,
                order: 500
            }
        },
        {
            headerName: 'Accession',
            field: 'accessionNumber',
            filterFieldDef: {
                type: FilterFieldType.MultiSelect,
                order: 600
            }
        },
        {
            headerName: 'Test Group',
            field: 'testGroupName',
            filterFieldDef: {
                type: FilterFieldType.MultiSelect,
                order: 700
            }
        },
        {
            headerName: 'Test',
            field: 'testDescription',
            filterFieldDef: {
                type: FilterFieldType.MultiSelect,
                order: 800
            }
        }
    ];

    private gramStainColumns: FilterGridColDef[] = [
        {
            headerName: 'Specimen',
            field: 'specimen',
            filterFieldDef: {
                order: 900,
                type: FilterFieldType.MultiSelect
            }
        },
        {
            headerName: 'Findings/Observed Results',
            field: 'result',

            valueGetter: (params: MicrobiologyGridRowItem) => this.onScreenResultsRenderer.getValue(params.data, entity => entity.result, true),
            getLineCount: (entity: MicrobiologyData) => {
                const result = this.onScreenResultsRenderer.getValue(entity, e => e.result);
                return this.multilineFormatter.format(result).length;
            },

            cellRendererFramework: MicrobiologyResultAgRendererComponent,

            filterFieldDef: {
                order: 1000,
                type: FilterFieldType.MultiSelect,
                isMatch: (selectedValues: string[], entity: MicrobiologyData) => {
                    const result = this.onScreenResultsRenderer.getValue(entity, e => e.result);
                    return some(selectedValues, val => val === result);
                },
                getItems: (entities: MicrobiologyData[]) => {
                    const items = chain(entities)
                        .map((entity: MicrobiologyData) => this.onScreenResultsRenderer.getValue(entity, e => e.result))
                        .uniq()
                        .sortBy()
                        .value();

                    return items;
                }
            }
        }
    ];

    private isolateCultureColumns: FilterGridColDef[] = [
        {
            headerName: 'Specimen',
            field: 'specimen',
            filterFieldDef: {
                order: 900,
                type: FilterFieldType.MultiSelect
            }
        },
        {
            headerName: 'Organism',
            field: 'organism',

            getLineCount: (entity: MicrobiologyData) => {
                return this.multilineFormatter.format(entity.fullOrganismTitle).length;
            },

            cellRendererFramework: MultilineAgRendererComponent,

            filterFieldDef: {
                order: 1000,
                type: FilterFieldType.MultiSelect
            }
        },
        {
            headerName: 'Growth',
            field: 'growth',
            filterFieldDef: {
                order: 1100,
                type: FilterFieldType.MultiSelect
            }
        },
        {
            headerName: 'Drug/Agent',
            field: 'agent',
            filterFieldDef: {
                order: 1200,
                type: FilterFieldType.MultiSelect
            }

        },
        {
            headerName: 'Units',
            field: 'units',
            filterFieldDef: {
                order: 1200,
                type: FilterFieldType.MultiSelect
            }

        },
        {
            headerName: 'MIC Panel Result ',
            field: 'micResult',
            filterFieldDef: {
                isOff: true
            }
        },
        {
            headerName: 'MIC Panel Interpretation',
            field: 'micPanelInterpretation',
            filterFieldDef: {
                order: 1400,
                type: FilterFieldType.MultiSelect
            }
        },
        {
            headerName: 'Disk Diffusion Panel Zone mm',
            field: 'kirbyBauerPanelZone',
            filterFieldDef: {
                isOff: true
            }
        },
        {
            headerName: 'Disk Diffusion Interpretation',
            field: 'kirbyBauerInterpretation',
            filterFieldDef: {
                order: 1600,
                type: FilterFieldType.MultiSelect
            }
        },
        {
            headerName: 'Observations',
            field: 'observations',
            filterFieldDef: {
                isOff: true
            }
        }
    ];

    constructor(
        private multilineFormatter: MultilineFormatter,
        private onScreenResultsRenderer: OnScreenResultsRenderer
    ) { }

    private getVisitDescriptionRenderer(data: MicrobiologyData) {
        if (!data) {
            return null;
        }

        return !isEmpty(data.visitDescription) ? data.visitDescription : data.kitTypeDescription;
    }
}
